import type { DBSchema, IDBPDatabase } from 'idb';
import { openStore, openWithin, reportOpenFailure } from './storeOpen.js';
import type { FieldKeyString, FieldValue, Hlc, Uuid } from '@colplat/protocol';
import { uuidv7 } from './uuid.js';

/**
 * Offline write queue in IndexedDB, replayed in HLC order on reconnect.
 *
 * An online write whose ack never arrives before the socket drops is pushed
 * into this same outbox with its original base/clock — an unacked live write
 * and an offline write are the same code path, so there is only one retry
 * mechanism, not two.
 */

export interface OutboxWrite {
  id: Uuid;
  key: FieldKeyString;
  value: FieldValue;
  base: number;
  clock: Hlc;
  /** Always absent: offline editing holds no lock (ADR-0011). */
  leaseId?: Uuid;
  queuedAt: string;
  attempts: number;
  hlcSortKey: string;
}

/**
 * A whole document *group* queued offline — a parent plus its children
 * committed together (ADR-0021), e.g. a transaction with its splits. Neither
 * `OutboxWrite` (one field) nor `OutboxRecordWrite` (one record's fields) can
 * express one, and a group commit has no field key to supersede on.
 *
 * `payload` is opaque here on purpose. Group commits travel over REST, not
 * the WebSocket (ADR-0021), so the endpoint and body shape belong to the app
 * that owns them; core-client owns the durability, the ordering and the
 * replay bookkeeping. Keeping the shape out of this package is what stops
 * every app's request format leaking into the sync client.
 */
export interface OutboxDocumentWrite {
  id: Uuid;
  entity: string;
  recordId: Uuid;
  /**
   * `restore` carries no payload at all: putting a record back is the same
   * request whatever it holds, and it cannot destroy anything, so there is
   * nothing to compare-and-swap against.
   *
   * `delete` carries no field values — its payload is the `baseVersion` the
   * record was at when somebody deleted it, which is what the server
   * compare-and-swaps against so an offline delete cannot land on top of
   * somebody else's later edit (invariant #2).
   */
  op: 'create' | 'update' | 'delete' | 'restore';
  payload: unknown;
  clock: Hlc;
  queuedAt: string;
  attempts: number;
  hlcSortKey: string;
  /**
   * Set when this entry is not work on its way to the server but a draft
   * that was interrupted: somebody took the record over mid-edit, so these
   * values are the user's and the decision about them is the user's too.
   *
   * A held entry is **never flushed** — see `sendableDocuments`. That is the
   * difference between it and every other outbox entry, and the reason it
   * lives here rather than in its own store: it is the same unsent work,
   * needing the same durability (a browser crash must not lose it), reviewed
   * through the same compare/apply/discard path as an offline edit the
   * server refused. The two are the same situation reached by different
   * routes (ADR-0011).
   */
  held?: HeldRevision;
  /**
   * The server answered this entry with a refusal that retrying will not
   * change — a rule it breaks, in the server's own words. It stays (dropping
   * somebody's work is the worst outcome there is) but is not sent again
   * until it is changed or thrown away: saving it again queues a fresh entry
   * without this. Shown, not just counted (ADR-0011, 2026-10-21).
   */
  refused?: { message: string; at: string };
}

/** Who took the record, and when, so the review can say so. */
export interface HeldRevision {
  takenBy: Uuid;
  takenByName: string;
  at: string;
}

/** Whole-record document-mode offline edits — no per-field key exists for these. */
export interface OutboxRecordWrite {
  id: Uuid;
  entity: string;
  recordId: Uuid;
  fields: Record<string, FieldValue>;
  baseVersion: number;
  clock: Hlc;
  queuedAt: string;
  attempts: number;
}

interface OutboxSchema extends DBSchema {
  outbox: {
    key: Uuid;
    value: OutboxWrite;
    indexes: { by_key: FieldKeyString; by_hlc: string };
  };
  outboxRecords: {
    key: Uuid;
    value: OutboxRecordWrite;
    indexes: { by_record: [string, string] };
  };
  outboxDocuments: {
    key: Uuid;
    value: OutboxDocumentWrite;
    indexes: { by_record: [string, string]; by_hlc: string };
  };
}

/** IndexedDB indexes can't sort a composite {wall, counter, node} tuple, so precompute a sortable string. */
function hlcSortKey(clock: Hlc): string {
  return `${String(clock.wall).padStart(15, '0')}-${String(clock.counter).padStart(10, '0')}-${clock.node}`;
}

export interface OutboxQueueOptions {
  appId: string;
  env: 'prod' | 'test';
}

export class OutboxQueue {
  private db: IDBPDatabase<OutboxSchema> | undefined;

  constructor(private readonly opts: OutboxQueueOptions) {}

  /**
   * Opening, awaited by every method rather than by the page (2026-10-21).
   *
   * The outbox is the one store that cannot be swapped for a temporary copy
   * — it holds unsent work — so when it is slow to open the page carries on
   * without it and whatever needs it waits for it. Before, the whole page
   * waited: a store held up by another tab drew headers and no data.
   */
  private opening: Promise<IDBPDatabase<OutboxSchema>> | undefined;

  async open(opts: { waitAtMostMs?: number } = {}): Promise<void> {
    const dbName = `colplat-outbox-${this.opts.appId}-${this.opts.env}`;
    // Version 2 adds `outboxDocuments`. The upgrade is additive and guarded
    // per store, so an existing database keeps whatever is already queued —
    // the outbox is the one client-side store whose loss is real data loss
    // (see replica.ts), so an upgrade must never recreate it from scratch.
    this.opening = openStore<OutboxSchema>(dbName, 2, (db) => {
        if (!db.objectStoreNames.contains('outbox')) {
          const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
          outbox.createIndex('by_key', 'key');
          outbox.createIndex('by_hlc', 'hlcSortKey');
        }
        if (!db.objectStoreNames.contains('outboxRecords')) {
          const records = db.createObjectStore('outboxRecords', { keyPath: 'id' });
          records.createIndex('by_record', ['entity', 'recordId']);
        }
        if (!db.objectStoreNames.contains('outboxDocuments')) {
          const documents = db.createObjectStore('outboxDocuments', { keyPath: 'id' });
          documents.createIndex('by_record', ['entity', 'recordId']);
          documents.createIndex('by_hlc', 'hlcSortKey');
        }
    });
    void this.opening.then((db) => { this.db = db; }).catch((err: unknown) => { reportOpenFailure(dbName, err); });
    if (opts.waitAtMostMs === undefined) { this.db = await this.opening; return; }
    // Not thrown from here when it fails: the page carries on, and whatever
    // needs the outbox gets the failure itself (and the report says why).
    await openWithin(this.opening, opts.waitAtMostMs, dbName).catch(() => undefined);
  }

  private async requireDb(): Promise<IDBPDatabase<OutboxSchema>> {
    if (this.db) return this.db;
    if (!this.opening) throw new Error('OutboxQueue.open() must be called before use');
    return this.opening;
  }

  /** A later offline edit to the same field supersedes the earlier queued one. */
  async enqueue(w: Omit<OutboxWrite, 'id' | 'queuedAt' | 'attempts' | 'hlcSortKey'>): Promise<OutboxWrite> {
    const db = (await this.requireDb());
    const tx = db.transaction('outbox', 'readwrite');
    const existingKey = await tx.store.index('by_key').getKey(w.key);
    if (existingKey !== undefined) await tx.store.delete(existingKey);

    const entry: OutboxWrite = {
      ...w, id: uuidv7(), queuedAt: new Date().toISOString(), attempts: 0, hlcSortKey: hlcSortKey(w.clock),
    };
    await tx.store.put(entry);
    await tx.done;
    return entry;
  }

  /** Ascending by HLC — this IS "replay in HLC order." */
  async all(): Promise<OutboxWrite[]> {
    return (await this.requireDb()).getAllFromIndex('outbox', 'by_hlc');
  }

  async remove(id: Uuid): Promise<void> {
    await (await this.requireDb()).delete('outbox', id);
  }

  async bumpAttempts(id: Uuid): Promise<void> {
    const db = (await this.requireDb());
    const tx = db.transaction('outbox', 'readwrite');
    const entry = await tx.store.get(id);
    if (entry) {
      entry.attempts += 1;
      await tx.store.put(entry);
    }
    await tx.done;
  }

  /** For a "you have unsynced work" nudge. */
  async oldestQueuedAt(): Promise<string | undefined> {
    const all = await this.all();
    if (all.length === 0) return undefined;
    return all.reduce((min, w) => (w.queuedAt < min ? w.queuedAt : min), all[0]!.queuedAt);
  }

  async count(): Promise<number> {
    return (await this.requireDb()).count('outbox');
  }

  // ------------------------------------------------------- document-mode records

  async enqueueRecord(
    w: Omit<OutboxRecordWrite, 'id' | 'queuedAt' | 'attempts'>,
  ): Promise<OutboxRecordWrite> {
    const entry: OutboxRecordWrite = { ...w, id: uuidv7(), queuedAt: new Date().toISOString(), attempts: 0 };
    await (await this.requireDb()).put('outboxRecords', entry);
    return entry;
  }

  async allRecords(): Promise<OutboxRecordWrite[]> {
    return (await this.requireDb()).getAll('outboxRecords');
  }

  async removeRecord(id: Uuid): Promise<void> {
    await (await this.requireDb()).delete('outboxRecords', id);
  }

  // ------------------------------------------------------ document groups

  /**
   * Queue a group commit. Re-saving the same record offline replaces its
   * queued entry rather than adding a second one — the user edited a draft
   * twice, they did not ask to create two transactions. (`enqueue` does the
   * same for a field, for the same reason.)
   */
  async enqueueDocument(
    w: Omit<OutboxDocumentWrite, 'id' | 'queuedAt' | 'attempts' | 'hlcSortKey'>,
  ): Promise<OutboxDocumentWrite> {
    const db = (await this.requireDb());
    const tx = db.transaction('outboxDocuments', 'readwrite');
    const existingKey = await tx.store.index('by_record').getKey([w.entity, w.recordId]);
    if (existingKey !== undefined) await tx.store.delete(existingKey);

    const entry: OutboxDocumentWrite = {
      ...w, id: uuidv7(), queuedAt: new Date().toISOString(), attempts: 0, hlcSortKey: hlcSortKey(w.clock),
    };
    await tx.store.put(entry);
    await tx.done;
    return entry;
  }

  /**
   * Everything queued, ascending by HLC — the same replay order the field
   * outbox uses. Includes held entries, so callers deciding what to *send*
   * want `sendableDocuments` instead.
   */
  async allDocuments(): Promise<OutboxDocumentWrite[]> {
    return (await this.requireDb()).getAllFromIndex('outboxDocuments', 'by_hlc');
  }

  /**
   * What a flush may actually send. Held entries are excluded here rather
   * than at each call site: a flusher that forgets the distinction would
   * push a draft the user never chose to send, over the very person who
   * took the record from them.
   */
  async sendableDocuments(): Promise<OutboxDocumentWrite[]> {
    return (await this.allDocuments()).filter((d) => !d.held && !d.refused);
  }

  /** Record the server's refusal on an entry, which stops it being sent again. See `refused`. */
  async markDocumentRefused(id: Uuid, message: string): Promise<void> {
    const db = (await this.requireDb());
    const tx = db.transaction('outboxDocuments', 'readwrite');
    const entry = await tx.store.get(id);
    if (entry) {
      entry.attempts += 1;
      entry.refused = { message, at: new Date().toISOString() };
      await tx.store.put(entry);
    }
    await tx.done;
  }

  /** Drafts waiting on a human decision. */
  async heldDocuments(): Promise<OutboxDocumentWrite[]> {
    return (await this.allDocuments()).filter((d) => d.held !== undefined);
  }

  async removeDocument(id: Uuid): Promise<void> {
    await (await this.requireDb()).delete('outboxDocuments', id);
  }

  async bumpDocumentAttempts(id: Uuid): Promise<void> {
    const db = (await this.requireDb());
    const tx = db.transaction('outboxDocuments', 'readwrite');
    const entry = await tx.store.get(id);
    if (entry) {
      entry.attempts += 1;
      await tx.store.put(entry);
    }
    await tx.done;
  }
}
